import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { Modal } from "reactstrap";
import "../../../assets/css/feed-responsive.css";
import "../../../assets/css/upload-modal-raw.css";
import "./upload-content.css";
import { useTheme } from "../../../features/contexts/ThemeContext";
import { 
  ROLE, 
  UPLOAD_CONTENT_TYPE, 
  CONTENT_PRICING, 
  MEDIA_TYPE, 
  PAYMENT_INFO,
  MATRIX_CONTENT_TYPE
} from "../../../shared/constants/common";
import { UPLOAD_CONTENT } from "../../../shared/constants/valisdation";
import {
  UPLOAD_MEDIA_ACCEPT,
  UPLOAD_MEDIA_FORMATS_LABEL,
} from "../../../shared/constants/media";
import { getUserRole, isVideo } from "../../../shared/utils/function";
import { validateFileSize } from "../../../shared/utils/fileValidation";
import {
  prepareUploadFileObjs,
  revokeMediaPreviewUrl,
  preloadHeicDecoder,
} from "../../../shared/utils/mediaPreview";
import { showSuccess, showError } from "../../../shared/utils/swalHandler";
import CameraCapture from "../../../components/CameraCapture";
import ContentCaptionSection from "./ContentCaptionSection";
import FilePreviewMedia from "./FilePreviewMedia";
import generateVideoPoster from "./generateVideoPoster";
import { PostContent } from "../../../components/UploadContent";
import { reportError } from "../../../shared/sentry";
import { useUploadBackgroundGuard } from "../../../shared/hooks/useUploadBackgroundGuard";
import { normalizeAssetsForMatrix } from "../../../shared/utils/signedUrlCache";

// Helper functions
const revokePreviewUrl = (fileObj) => revokeMediaPreviewUrl(fileObj);
const revokePreviewUrls = (fileObjs = []) => {
  fileObjs.forEach(revokePreviewUrl);
};

const attachVideoPosters = (fileObjs, updateFiles) => {
  fileObjs.forEach((fileObj) => {
    if (!fileObj.previewUrl || !isVideo(fileObj.file) || fileObj.posterUrl) {
      return;
    }

    generateVideoPoster(fileObj.previewUrl, fileObj.file).then((posterUrl) => {
      if (!posterUrl) return;

      updateFiles((prev) =>
        prev.map((item) =>
          item.previewUrl === fileObj.previewUrl
            ? { ...item, posterUrl }
            : item
        )
      );
    });
  });
};

const setFilesAndGeneratePosters = async (fileObjs, setFiles) => {
  const withPreviews = await prepareUploadFileObjs(fileObjs);
  setFiles(withPreviews);
  attachVideoPosters(withPreviews, setFiles);
};

const getPriceDisplay = (price, canCharge) => {
  if (!canCharge) return "$0";
  return price ? `$${price}` : "$0";
};

const shouldShowPriceBadge = (selectedOption, userRole) => {
  return !(selectedOption === UPLOAD_CONTENT_TYPE.FREE || userRole === ROLE.CREATOR_SUB);
};

const UploadContent = ({
  PostContent,
  isDarkMode,
  forFooter,
  forCreatorMessaging,
  purposeDest,
  onUploadSuccess,
  triggerElement,
}) => {
  // Get user role
  const userRole = getUserRole();
  const canCharge = userRole === ROLE.CREATOR_CONTENT || userRole === ROLE.CREATOR_SUB;

  // State management
  const [modal, setModal] = useState(false);
  const [allFiles, setAllFiles] = useState([]);
  const [bundleFiles, setBundleFiles] = useState([]);
  const [locked, setLocked] = useState(canCharge);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(canCharge ? UPLOAD_CONTENT_TYPE.PAID : UPLOAD_CONTENT_TYPE.FREE);
  const [visibleFilesCount, setVisibleFilesCount] = useState(UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT);
  const [visibleBundleFilesCount, setVisibleBundleFilesCount] = useState(UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT);
  const [showCamera, setShowCamera] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [filePrices, setFilePrices] = useState({});
  const [priceInputs, setPriceInputs] = useState({});
  const [bundleCaption, setBundleCaption] = useState("");
  const [bundlePrice, setBundlePrice] = useState("");
  const [bundlePriceInput, setBundlePriceInput] = useState("");
  const [selectedTaggedUsersBundle, setSelectedTaggedUsersBundle] = useState([]);
  const [selectedTaggedUsersByFile, setSelectedTaggedUsersByFile] = useState({});
  const [priceValidationAttempted, setPriceValidationAttempted] = useState(false);
  const allFilesRef = useRef(allFiles);
  const bundleFilesRef = useRef(bundleFiles);

  useUploadBackgroundGuard(loading);

  useEffect(() => {
    allFilesRef.current = allFiles;
  }, [allFiles]);

  useEffect(() => {
    bundleFilesRef.current = bundleFiles;
  }, [bundleFiles]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      revokePreviewUrls(allFilesRef.current);
      revokePreviewUrls(bundleFilesRef.current);
      setAllFiles([]);
      setBundleFiles([]);
    };
  }, []);

  // Toggle modal
  const toggleModal = useCallback(() => {
    setModal((prev) => {
      if (!prev) {
        preloadHeicDecoder();
      }
      return !prev;
    });
    setLocked(canCharge);
    setVisibleFilesCount(UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT);
    setVisibleBundleFilesCount(UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT);
    setSelectedOption(canCharge ? UPLOAD_CONTENT_TYPE.PAID : UPLOAD_CONTENT_TYPE.FREE);
    // Reset price-related states when opening modal
    setFilePrices({});
    setPriceInputs({});
    setBundlePrice("");
    setBundlePriceInput("");
    setPriceValidationAttempted(false);
  }, [canCharge]);

  // Close modal and reset
  const closeBtn = useCallback(() => {
    clearFiles();
    toggleModal();
    setLocked(canCharge);
    setVisibleFilesCount(UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT);
    setVisibleBundleFilesCount(UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT);
    setShowCamera(false);
    setUploadProgress(0);
    setSelectedOption(canCharge ? UPLOAD_CONTENT_TYPE.PAID : UPLOAD_CONTENT_TYPE.FREE);
    setSelectedTaggedUsersBundle([]);
    setSelectedTaggedUsersByFile({});
    // Reset price-related states
    setFilePrices({});
    setPriceInputs({});
    setPriceValidationAttempted(false);
  }, [canCharge, toggleModal]);

  // Clear all files
  const clearFiles = useCallback(() => {
    revokePreviewUrls(allFiles);
    revokePreviewUrls(bundleFiles);
    setAllFiles([]);
    setBundleFiles([]);
    setVisibleFilesCount(UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT);
    setVisibleBundleFilesCount(UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT);
    setBundleCaption("");
    setBundlePrice("");
    setBundlePriceInput("");
    setSelectedTaggedUsersBundle([]);
    setSelectedTaggedUsersByFile({});
  }, []);

  // Reset helpers for mutually exclusive selections
  const resetBundleSelection = useCallback(() => {
    revokePreviewUrls(bundleFiles);
    setBundleFiles([]);
    setVisibleBundleFilesCount(UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT);
    setBundleCaption("");
    setBundlePrice("");
    setBundlePriceInput("");
    setSelectedTaggedUsersBundle([]);
  }, []);

  const resetRegularSelection = useCallback(() => {
    revokePreviewUrls(allFiles);
    setAllFiles([]);
    setVisibleFilesCount(UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT);
    setFilePrices({});
    setPriceInputs({});
    setSelectedTaggedUsersByFile({});
  }, []);

  // Validate file constraints - uses shared validation utilities
  const validateUploadFiles = useCallback(async (newFiles, existingFiles) => {
    const combinedFiles = [...existingFiles, ...newFiles];

    // Check file limit
    if (combinedFiles.length > UPLOAD_CONTENT.MAX_FILES) {
      await showError(
        "Too Many Files",
        `You can only upload up to ${UPLOAD_CONTENT.MAX_FILES} files at a time. Please remove some files first.`
      );
      return null;
    }

    // Check file size limit using shared validation
    const validatedFiles = [];

    for (const fileObj of newFiles) {
      const isValid = await validateFileSize(fileObj.file, UPLOAD_CONTENT.MAX_SIZE_MB);
      if (isValid) {
        validatedFiles.push(fileObj);
      }
    }

    if (validatedFiles.length === 0) {
      return null;
    }

    const combinedValidatedFiles = [...existingFiles, ...validatedFiles];

    // Remove duplicates based on name, size, and lastModified
    const uniqueFiles = combinedValidatedFiles.filter((fileObj, index, self) =>
      index === self.findIndex(f =>
        f.file.name === fileObj.file.name &&
        f.file.size === fileObj.file.size &&
        f.file.lastModified === fileObj.file.lastModified
      )
    );

    return uniqueFiles;
  }, []);

  // Handle bundle file selection - uses shared validation utilities
  const handleBundleFileChange = useCallback(async (e) => {
    const input = e.target;
    const files = input?.files ? Array.from(input.files) : [];
    if (input) input.value = "";

    // Switching to bundle: clear any previously selected regular (camera/library) files
    resetRegularSelection();

    if (files.length === 0) {
      await showError(
        "No files selected",
        "Please select one or more videos or images for the bundle. If you selected files and nothing happened, try choosing them again."
      );
      return;
    }

    const newFiles = files.map(file => ({ file, caption: "" }));
    const uniqueFiles = await validateUploadFiles(newFiles, bundleFiles);

    if (uniqueFiles) {
      await setFilesAndGeneratePosters(uniqueFiles, setBundleFiles);
      setVisibleBundleFilesCount(Math.min(uniqueFiles.length, UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT));
    }
  }, [bundleFiles, validateUploadFiles, resetRegularSelection]);

  // Remove bundle file
  const removeBundleFile = useCallback((index) => {
    const updatedFiles = bundleFiles.filter((_, i) => i !== index);
    revokePreviewUrl(bundleFiles[index]);
    setBundleFiles(updatedFiles);
    setVisibleBundleFilesCount(Math.min(updatedFiles.length, visibleBundleFilesCount));
  }, [bundleFiles, visibleBundleFilesCount]);

  // Handle caption change
  const handleChangeCaption = useCallback((e, index) => {
    const updatedFiles = [...allFiles];
    updatedFiles[index].caption = e.target.value;
    setAllFiles(updatedFiles);
  }, [allFiles]);

  // Handle file selection - uses shared validation utilities
  const handleFileChange = useCallback(async (e) => {
    const input = e.target;
    const files = input?.files ? Array.from(input.files) : [];
    // Reset input value so re-selecting the same file (e.g. from Favorites) triggers onChange again
    if (input) input.value = "";

    // Switching to library/camera selection: clear any previously selected bundle files
    resetBundleSelection();

    if (files.length === 0) {
      await showError(
        "No files selected",
        "Please select one or more videos or images from your library. If you selected a file and nothing happened, try choosing it again."
      );
      return;
    }

    // In creator messaging (non-bundle) mode, only allow a single media.
    // If a file is already selected, replace it when a new one is chosen.
    if (forCreatorMessaging && bundleFiles.length === 0) {
      const singleFile = files[0];
      const newFileObj = { file: singleFile, caption: "" };
      const uniqueFiles = await validateUploadFiles([newFileObj], []);

      if (uniqueFiles) {
        // Replace any existing previews with the new single file
        revokePreviewUrls(allFiles);
        await setFilesAndGeneratePosters(uniqueFiles, setAllFiles);
        setVisibleFilesCount(Math.min(uniqueFiles.length, UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT));
      }
      return;
    }

    const newFiles = files.map(file => ({ file, caption: "" }));
    const uniqueFiles = await validateUploadFiles(newFiles, allFiles);

    if (uniqueFiles) {
      await setFilesAndGeneratePosters(uniqueFiles, setAllFiles);
      setVisibleFilesCount(Math.min(uniqueFiles.length, UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT));
    }
  }, [allFiles, bundleFiles.length, forCreatorMessaging, validateUploadFiles, resetBundleSelection]);

  // Remove file
  const removeFile = useCallback((index) => {
    revokePreviewUrl(allFiles[index]);
    const updatedFiles = allFiles.filter((_, i) => i !== index);
    setAllFiles(updatedFiles);

    // Reindex per-file state maps to keep them aligned with files array
    setSelectedTaggedUsersByFile((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        const k = Number(key);
        if (Number.isNaN(k)) return;
        if (k < index) {
          next[k] = prev[k];
        } else if (k > index) {
          next[k - 1] = prev[k];
        }
        // if k === index -> drop it (clears tags for removed slot)
      });
      return next;
    });

    // Keep prices and inputs aligned with files after deletion
    setFilePrices((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        const k = Number(key);
        if (Number.isNaN(k)) return;
        if (k < index) {
          next[k] = prev[k];
        } else if (k > index) {
          next[k - 1] = prev[k];
        }
      });
      return next;
    });

    setPriceInputs((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        const k = Number(key);
        if (Number.isNaN(k)) return;
        if (k < index) {
          next[k] = prev[k];
        } else if (k > index) {
          next[k - 1] = prev[k];
        }
      });
      return next;
    });

    setVisibleFilesCount(Math.min(updatedFiles.length, visibleFilesCount));
  }, [allFiles, visibleFilesCount]);

  // Load more files
  const loadMoreFiles = useCallback(() => {
    setVisibleFilesCount(prev => prev + UPLOAD_CONTENT.LOAD_MORE_INCREMENT);
  }, []);

  // Load more bundle files
  const loadMoreBundleFiles = useCallback(() => {
    setVisibleBundleFilesCount(prev => prev + UPLOAD_CONTENT.LOAD_MORE_INCREMENT);
  }, []);

  // Change locked/unlocked content
  const changeLocked = useCallback((elm) => {
    const select = elm.target.value;

    if (select === UPLOAD_CONTENT_TYPE.PAID && !canCharge) {
      return;
    }

    if (select === UPLOAD_CONTENT_TYPE.PAID) {
      setLocked(true);
      setSelectedOption(UPLOAD_CONTENT_TYPE.PAID);
    }
    if (select === UPLOAD_CONTENT_TYPE.FREE) {
      setLocked(false);
      setSelectedOption(UPLOAD_CONTENT_TYPE.FREE);
    }
  }, [canCharge]);

  // Open camera
  const openCamera = useCallback(() => {
    // Switching to camera: clear any previously selected bundle files
    resetBundleSelection();
    setShowCamera(true);
  }, [resetBundleSelection]);

  // Open library (file picker) ensuring bundle files are cleared
  const openLibrary = useCallback(() => {
    resetBundleSelection();
    const input = document.getElementById("fileInput");
    if (input) input.click();
  }, [resetBundleSelection]);

  // Open bundle picker ensuring regular files are cleared
  const openBundlePicker = useCallback(() => {
    resetRegularSelection();
    const input = document.getElementById("bundleFileInput");
    if (input) input.click();
  }, [resetRegularSelection]);

  // Handle captured media from CameraCapture - uses shared validation utilities
  const handleCapturedMedia = useCallback(async (file) => {
    if (file) {
      const isValid = await validateFileSize(file, UPLOAD_CONTENT.MAX_SIZE_MB);

      if (!isValid) {
        setShowCamera(false);
        return;
      }

      // In creator messaging (non-bundle) mode, only allow a single media.
      // Replace any existing selection with the newly captured media.
      if (forCreatorMessaging && bundleFiles.length === 0) {
        revokePreviewUrls(allFiles);
        const newFileObj = (await prepareUploadFileObjs([{ file, caption: "" }]))[0];
        setAllFiles([newFileObj]);
        attachVideoPosters([newFileObj], setAllFiles);
        setVisibleFilesCount(1);
        setShowCamera(false);
        return;
      }

      const newFileObj = (await prepareUploadFileObjs([{ file, caption: "" }]))[0];
      setAllFiles((prev) => [...prev, newFileObj]);
      attachVideoPosters([newFileObj], setAllFiles);
      setVisibleFilesCount(prev => Math.min(allFiles.length + 1, UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT));
      setShowCamera(false);
    }
  }, [allFiles, bundleFiles.length, forCreatorMessaging]);

  // Handle price input change
  const handlePriceInputChange = useCallback((index, value) => {
    // Remove $ symbol if present
    const cleanValue = value.replace(/\$/g, '');

    // Allow empty input
    if (cleanValue === '') {
      setPriceInputs(prev => ({
        ...prev,
        [index]: ''
      }));
      return;
    }

    // Validate numeric input (allow numbers and one decimal point)
    const numericRegex = /^\d*\.?\d{0,2}$/;
    if (numericRegex.test(cleanValue)) {
      setPriceInputs(prev => ({
        ...prev,
        [index]: cleanValue
      }));
    }
  }, []);

  // Handle Set button click
  const handleSetButtonClick = useCallback(async (index) => {
    const currentPrice = filePrices[index];
    const isPriceSet = !!(currentPrice && currentPrice !== "" && currentPrice !== "0");

    if (isPriceSet) {
      // Unset the price
      setFilePrices(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      setPriceInputs(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      return;
    }

    // Set the price with validation
    const priceValue = parseFloat(priceInputs[index] || "0");

    if (isNaN(priceValue) || priceValue <= 0) {
      await showError(
        "Invalid Price",
        "Please enter a valid price."
      );
      return;
    }

    if (priceValue < CONTENT_PRICING.MIN_PRICE) {
      await showError(
        "Minimum Price",
        `The minimum price is $${CONTENT_PRICING.MIN_PRICE.toFixed(2)}`
      );
      return;
    }

    if (priceValue > CONTENT_PRICING.MAX_PRICE) {
      await showError(
        "Maximum Price",
        `The maximum price you can set is $${CONTENT_PRICING.MAX_PRICE} USD.`
      );
      return;
    }

    const formattedPrice = priceValue.toFixed(2);
    setFilePrices(prev => ({
      ...prev,
      [index]: formattedPrice
    }));
    // Keep the input value so the field doesn't become blank
    setPriceInputs(prev => ({
      ...prev,
      [index]: formattedPrice
    }));
  }, [filePrices, priceInputs]);

  // Handle bundle caption change
  const handleBundleCaptionChange = useCallback((e) => {
    setBundleCaption(e.target.value);
  }, []);

  // Handle bundle price input change
  const handleBundlePriceInputChange = useCallback((e) => {
    const value = e.target.value;

    // Remove $ symbol if present
    const cleanValue = value.replace(/\$/g, '');

    // Allow empty input
    if (cleanValue === '') {
      setBundlePriceInput('');
      return;
    }

    // Validate numeric input (allow numbers and one decimal point)
    const numericRegex = /^\d*\.?\d{0,2}$/;
    if (numericRegex.test(cleanValue)) {
      setBundlePriceInput(cleanValue);
    }
  }, []);

  // Handle bundle Set button click
  const handleBundleSetButtonClick = useCallback(async () => {
    const isPriceSet = !!(bundlePrice && bundlePrice !== "" && bundlePrice !== "0");

    if (isPriceSet) {
      // Unset the price
      setBundlePrice("");
      setBundlePriceInput("");
      return;
    }

    // Set the price with validation
    const priceValue = parseFloat(bundlePriceInput || "0");

    if (isNaN(priceValue) || priceValue <= 0) {
      await showError(
        "Invalid Price",
        "Please enter a valid price."
      );
      return;
    }

    if (priceValue < CONTENT_PRICING.MIN_PRICE) {
      await showError(
        "Minimum Price",
        `The minimum price is $${CONTENT_PRICING.MIN_PRICE.toFixed(2)}.`
      );
      return;
    }

    if (priceValue > CONTENT_PRICING.MAX_PRICE) {
      await showError(
        "Maximum Price",
        `The maximum price you can set is $${CONTENT_PRICING.MAX_PRICE} USD.`
      );
      return;
    }

    const formattedPrice = priceValue.toFixed(2);
    setBundlePrice(formattedPrice);
    // Keep the input value so the field doesn't become blank
    setBundlePriceInput(formattedPrice);
  }, [bundlePrice, bundlePriceInput]);

  // Handle tagged users change for files
  const handleTaggedUsersChange = useCallback((fileIndex, selectedUsers) => {
    setSelectedTaggedUsersByFile(prev => ({
      ...prev,
      [fileIndex]: selectedUsers
    }));
  }, []);

  // Handle tagged users change for bundle
  const handleBundleTaggedUsersChange = useCallback((selectedUsers) => {
    setSelectedTaggedUsersBundle(selectedUsers);
  }, []);

  // Handle upload content
  const handleUploadContent = useCallback(async () => {
    const totalFiles = allFiles.length + bundleFiles.length;
    let completedFiles = 0;
    let failedUploads = 0;
    let hasError = false;

    const getTagIdsForBundle = () => ((selectedTaggedUsersBundle || []).map(u => u._id || u.id).filter(Boolean));
    const getTagIdsForFile = (idx) => (((selectedTaggedUsersByFile && selectedTaggedUsersByFile[idx]) || []).map(u => u._id || u.id).filter(Boolean));

    // Track each upload job's progress independently so simultaneous uploads
    // cannot regress the displayed value by overwriting each other's progress.
    // totalUploadJobs = one entry per PostContent call (bundle counts as 1 job).
    const totalUploadJobs = allFiles.length + (bundleFiles.length > 0 ? 1 : 0);
    const uploadProgressMap = {};
    for (let i = 0; i < totalUploadJobs; i++) {
      uploadProgressMap[i] = 0;
    }
    const computeOverallProgress = () => {
      if (totalUploadJobs === 0) return 0;
      const total = Object.values(uploadProgressMap).reduce((sum, p) => sum + p, 0);
      return Math.round(total / totalUploadJobs);
    };

    // Validate prices for paid content
    // - Always for creator content accounts
    // - For creator messaging, also enforce pricing for monthly creators (CREATOR_SUB)
    // if (selectedOption === UPLOAD_CONTENT_TYPE.PAID && userRole === ROLE.CREATOR_CONTENT) {}
    const shouldValidatePrices =
      selectedOption === UPLOAD_CONTENT_TYPE.PAID &&
      (userRole === ROLE.CREATOR_CONTENT || (forCreatorMessaging && userRole === ROLE.CREATOR_SUB));

    if (shouldValidatePrices) {
      const missingPrices = [];
      allFiles.forEach((fileObj, index) => {
        if (!filePrices[index] || filePrices[index] === "" || filePrices[index] === "0") {
          missingPrices.push(`File ${index + 1}`);
        }
      });

      if (bundleFiles.length > 0 && (!bundlePrice || bundlePrice === "" || bundlePrice === "0")) {
        missingPrices.push("Bundle");
      }

      if (missingPrices.length > 0) {
        // await showError(
        //   "Price Required",
        //   `Please add a price for: ${missingPrices.join(", ")}`
        // );
        setPriceValidationAttempted(true);
        return;
      }
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      const promises = [];

      // Upload regular files
      allFiles.forEach((fileObj, index) => {
        const contentType = isVideo(fileObj.file) ? MEDIA_TYPE.VIDEO : MEDIA_TYPE.IMAGE;

        const upload = {
          src: URL.createObjectURL(fileObj.file),
          file: fileObj.file,
          caption: fileObj.caption,
          content_type: contentType,
          lock_content: selectedOption === UPLOAD_CONTENT_TYPE.PAID,
          price: canCharge && filePrices[index] ? filePrices[index] : "",
        };

        const fileTagIds = getTagIdsForFile(index);
        if (fileTagIds.length > 0) {
          upload.tagIds = fileTagIds;
        }

        if (forCreatorMessaging) {
          upload.purpose = "messaging";
          upload.purpose_dest = purposeDest;
        }

        const uploadPromise = PostContent(upload, (status, responseData) => {
          // Check if upload failed
          if (status === "failed") {
            hasError = true;
            failedUploads++;
          } else if (
            forCreatorMessaging &&
            typeof onUploadSuccess === "function" &&
            responseData
          ) {
            const matrixPayload = {
              type: MATRIX_CONTENT_TYPE.MEDIA,
              content_id: responseData?._id || null,
              purpose_dest: purposeDest,
              lock_content: upload.lock_content,
              price: upload.lock_content ? (upload.price || "") : "",
              extraCharge: PAYMENT_INFO.extraCharge ?? 0,
              caption: upload.caption || "",
              tag: Array.isArray(upload.tagIds) ? upload.tagIds : [],
              // Persist durable storage keys only — never signed URLs (they expire ~1h).
              assets: normalizeAssetsForMatrix(responseData?.assets),
            };

            onUploadSuccess(matrixPayload);
          }

          completedFiles++;
          uploadProgressMap[index] = 100;
          setUploadProgress(computeOverallProgress());
        }, (progress) => {
          uploadProgressMap[index] = progress;
          setUploadProgress(computeOverallProgress());
        });

        promises.push(uploadPromise);
      });

      // Upload bundle files
      if (bundleFiles.length > 0) {
        const bundleUpload = {
          files: bundleFiles.map(fileObj => ({
            src: URL.createObjectURL(fileObj.file),
            file: fileObj.file,
            caption: fileObj.caption || bundleCaption,
          })),
          caption: bundleCaption,
          lock_content: selectedOption === UPLOAD_CONTENT_TYPE.PAID,
          price: canCharge && bundlePrice ? bundlePrice : "",
          isBundle: true,
          content_type: 'bundle',
        };

        const bundleTagIds = getTagIdsForBundle();
        if (bundleTagIds.length > 0) {
          bundleUpload.tagIds = bundleTagIds;
        }

        if (forCreatorMessaging) {
          bundleUpload.purpose = "messaging";
          bundleUpload.purpose_dest = purposeDest;
        }

        const bundleJobIndex = allFiles.length; // bundle is always the last slot in progressMap
        const bundleUploadPromise = PostContent(bundleUpload, (status, responseData) => {
          // Check if upload failed
          if (status === "failed") {
            hasError = true;
            failedUploads++;
          } else if (
            forCreatorMessaging &&
            typeof onUploadSuccess === "function" &&
            responseData
          ) {
            const matrixPayload = {
              type: MATRIX_CONTENT_TYPE.MEDIA,
              content_id: responseData?._id || null,
              purpose_dest: purposeDest,
              lock_content: bundleUpload.lock_content,
              price: bundleUpload.lock_content ? (bundleUpload.price || "") : "",
              extraCharge: PAYMENT_INFO.extraCharge ?? 0,
              caption: bundleUpload.caption || "",
              tag: Array.isArray(bundleUpload.tagIds) ? bundleUpload.tagIds : [],
              // Persist durable storage keys only — never signed URLs (they expire ~1h).
              assets: normalizeAssetsForMatrix(responseData?.assets),
            };

            onUploadSuccess(matrixPayload);
          }

          completedFiles += bundleFiles.length;
          uploadProgressMap[bundleJobIndex] = 100;
          setUploadProgress(computeOverallProgress());
        }, (progress) => {
          uploadProgressMap[bundleJobIndex] = progress;
          setUploadProgress(computeOverallProgress());
        });

        promises.push(bundleUploadPromise);
      }

      // Wait for all uploads to complete
      await Promise.all(promises);

      // Check if all uploads completed successfully
      if (!hasError && failedUploads === 0) {
        // One-to-one chat: message appears in the thread; skip redundant success popup
        // if (!forCreatorMessaging) {
        //   await showSuccess(
        //     "Uploaded!",
        //     "Your content has been uploaded successfully."
        //   );
        // }

        // Reset state and close modal only on success
        revokePreviewUrls(allFiles);
        revokePreviewUrls(bundleFiles);
        setAllFiles([]);
        setBundleFiles([]);
        setVisibleFilesCount(UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT);
        setVisibleBundleFilesCount(UPLOAD_CONTENT.INITIAL_VISIBLE_COUNT);
        setFilePrices({});
        setPriceInputs({});
        setBundleCaption("");
        setBundlePrice("");
        setBundlePriceInput("");
        setSelectedTaggedUsersBundle([]);
        setSelectedTaggedUsersByFile({});

        if (onUploadSuccess) {
          onUploadSuccess();
        }

        closeBtn();
      } else {
        // Error occurred - don't close modal
        // If only 1 upload failed, the API error message is already shown by PostContent
        // Only show additional message if multiple uploads failed
        if (failedUploads > 1) {
          await showError(
            "Upload Failed",
            `${failedUploads} upload(s) failed. Please try again.`
          );
        }
        // For single failure, the error message from API is already displayed
      }
    } catch (error) {
      reportError(error, 'handleUploadContent');
      await showError(
        "Upload Error",
        "An unexpected error occurred. Please try again."
      );
    } finally {
      setLoading(false);
      // On success the modal is already closed by closeBtn() so this is a no-op visually.
      // On failure/error it clears the bar so the user can retry cleanly.
      setUploadProgress(0);
    }
  }, [
    allFiles,
    bundleFiles,
    selectedOption,
    userRole,
    filePrices,
    bundlePrice,
    canCharge,
    selectedTaggedUsersBundle,
    selectedTaggedUsersByFile,
    bundleCaption,
    forCreatorMessaging,
    purposeDest,
    PostContent,
    onUploadSuccess,
    closeBtn
  ]);

  // Display files (show all selected files)
  const displayedFiles = allFiles;

  return (
    <>
      {/* Trigger button / element */}
      {triggerElement
        ? React.cloneElement(triggerElement, {
            onClick: (e) => {
              if (typeof triggerElement.props.onClick === "function") {
                triggerElement.props.onClick(e);
              }
              if (!e.defaultPrevented) {
                toggleModal();
              }
            },
          })
        : forFooter && (
            <button
              className="upload-content-button"
              onClick={toggleModal}
              type="button"
            >
              <i className="fa fa-plus upload-content-button-color"></i>
            </button>
          )}

      {/* Modal */}
      {modal && (
        <Modal
          className="edit-profile-picure-modal upload-content "
          isOpen={modal}
          toggle={() => {}}
          backdrop="static"
          keyboard={false}
          centered
          modalClassName={isDarkMode ? "dark-mode-upload-modal" : ""}
        >
          <div className="upload-modal-header">
            <div className="upload-modal-header-content">
              <div className="header-spacer"></div>
              <h1 className="upload-modal-header-title">Upload Content</h1>
              <div className="header-spacer"></div>
            </div>
          </div>
          <div className="upload-modal-main">

            {/* Content Type Section */}
            <div className="content-type-section">
              <h3 className={`content-type-title content-type-title-margin ${!canCharge ? 'content-type-title-role1' : ''}`}>Content Type</h3>
              {canCharge && (
                <p className="content-type-subtitle">Choose Free or Paid before uploading content</p>
              )}
              <div className="content-type-buttons">
                {/* Free Content */}
                <button
                  className={`content-type-button ${selectedOption === UPLOAD_CONTENT_TYPE.FREE ? "active" : ""}`}
                  onClick={() => changeLocked({ target: { value: UPLOAD_CONTENT_TYPE.FREE } })}
                >
                  <div className="content-type-icon white-icon">
                    <i className="fa fa-unlock" />
                  </div>
                  <span className="content-type-label">Free</span>
                  <p className="content-type-description">Public content</p>
                </button>

                {/* Paid Content */}
                {canCharge && (
                  <button
                    className={`content-type-button ${selectedOption === UPLOAD_CONTENT_TYPE.PAID ? "active" : ""}`}
                    onClick={() => changeLocked({ target: { value: UPLOAD_CONTENT_TYPE.PAID } })}
                  >
                    <div className="content-type-icon white-icon">
                      <i className="fa fa-lock" />
                    </div>
                    <span className="content-type-label">Paid</span>
                    <p className="content-type-description">Premium content</p>
                  </button>
                )}
              </div>
            </div>

            <div className="upload-options-section">
              <h3 className="upload-options-title">Upload</h3>
              <div className="upload-options-grid">
                {/* Camera Box */}
                <button
                  className="upload-option-button"
                  onClick={openCamera}
                  disabled={loading}
                >
                  <div className="upload-option-content">
                    <div className="upload-option-icon white-icon">
                      <i className="fa fa-camera" />
                    </div>
                    <span className="upload-option-label">Camera</span>
                    <p className="upload-option-description">Take a photo or video</p>
                  </div>
                </button>

                {/* File Upload Box */}
                <button
                  className="upload-option-button"
                  onClick={openLibrary}
                  disabled={loading}
                >
                  <div className="upload-option-content">
                    <div className="upload-option-icon white-icon">
                      <i className="fa fa-images" />
                    </div>
                    <span className="upload-option-label">Library</span>
                    <p className="upload-option-description">Choose from library</p>
                  </div>
                </button>
              </div>

              <button
                className="upload-option-full"
                onClick={openBundlePicker}
                disabled={loading}
              >
                <div className="upload-option-content">
                  <div className="upload-option-icon white-icon">
                    <i className="fa fa-layer-group" />
                  </div>
                  <span className="upload-option-label">Bundle</span>
                  <p className="upload-option-description">Create content bundle</p>
                </div>
              </button>

              <input
                id="fileInput"
                type="file"
                multiple
                accept={UPLOAD_MEDIA_ACCEPT}
                onChange={handleFileChange}
                className="hidden-input"
              />
              <input
                id="bundleFileInput"
                type="file"
                multiple
                accept={UPLOAD_MEDIA_ACCEPT}
                onChange={handleBundleFileChange}
                className="hidden-input"
              />
            </div>

            {showCamera && (
              <div className="camera-fullscreen-overlay">
                <button
                  className="camera-close-button"
                  onClick={() => setShowCamera(false)}
                >
                  <i className="fa fa-times"></i>
                </button>
                <CameraCapture
                  onCapture={handleCapturedMedia}
                />
              </div>
            )}

            <div className="upload-tips-section">
              <div className="upload-tips-content">
                <div className="upload-tips-icon">
                  <i className="fa fa-info" />
                </div>
                <div className="upload-tips-text">
                  <h4 className="upload-tips-title">Upload Tips</h4>
                  <p>Supported formats: <b>{UPLOAD_MEDIA_FORMATS_LABEL}</b></p>
                  <p>Max size: <b>3GB</b></p>
                </div>
              </div>
            </div>

            {/* File Previews */}
            {(allFiles.length > 0 || bundleFiles.length > 0) && (
              <div className="selected-files-section">
                <div className="selected-files-header">
                  <h3 className="selected-files-title">Selected Files</h3>
                  <button
                    className="clear-all-button"
                    onClick={clearFiles}
                    disabled={loading}
                  >
                    Clear All
                  </button>
                </div>

                {/* Regular Files */}
                {allFiles.length > 0 && (
                  <div className="selected-files-grid">
                    {displayedFiles.map((fileObj, index) => (
                      <div
                        key={`preview-only-${index}`}
                        className="file-preview-item"
                      >
                        <FilePreviewMedia fileObj={fileObj} variant="grid" />

                        {shouldShowPriceBadge(selectedOption, userRole) && (
                          <div className={`file-preview-type-badge ${selectedOption === UPLOAD_CONTENT_TYPE.FREE ? "badge-pink" : "badge-green"}`}>
                            {selectedOption === UPLOAD_CONTENT_TYPE.FREE ? "Free" : getPriceDisplay(filePrices[index], canCharge)}
                          </div>
                        )}

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(index);
                          }}
                          className="file-preview-remove"
                        >
                          <i className="fa fa-xmark" />
                        </button>
                      </div>
                    ))}
                    {(allFiles.length < UPLOAD_CONTENT.MAX_FILES) && (
                      <button
                        className="add-more-button"
                        onClick={openLibrary}
                      >
                        <i className="fa fa-plus plus-icon-light"></i>
                      </button>
                    )}
                  </div>
                )}

                {/* Bundle Files */}
                {bundleFiles.length > 0 && (
                  <>
                    <p className="bundle-label">
                      Content Bundle {' '}
                      {shouldShowPriceBadge(selectedOption, userRole) ?
                        <span className="badge-paid-bundle-price">
                          ({getPriceDisplay(bundlePrice, canCharge)})
                        </span>
                        :
                        // <span className="badge-free-bundle-price">
                        //   (Free)
                        // </span>
                        null
                      }
                    </p>
                    <div className="selected-files-grid">
                      {bundleFiles.map((fileObj, index) => (
                        <div
                          key={`bundle-preview-${index}`}
                          className="file-preview-item"
                        >
                          <FilePreviewMedia fileObj={fileObj} variant="grid" />

                          {/* for bundle price badge */}
                          {/* {shouldShowPriceBadge(selectedOption, userRole) && (
                            <div className={`file-preview-type-badge ${selectedOption === UPLOAD_CONTENT_TYPE.FREE ? "badge-pink" : "badge-green"}`}>
                              {selectedOption === UPLOAD_CONTENT_TYPE.FREE ? "Free" : getPriceDisplay(bundlePrice, canCharge)}
                            </div>
                          )} */}

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeBundleFile(index);
                            }}
                            className="file-preview-remove"
                          >
                            <i className="fa fa-xmark" />
                          </button>
                        </div>
                      ))}
                      {(bundleFiles.length > 0 && bundleFiles.length < UPLOAD_CONTENT.MAX_FILES) && (
                        <button
                          className="add-more-button"
                          onClick={openBundlePicker}
                        >
                          <i className="fa fa-plus plus-icon-light"></i>
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Content Caption Section */}
            <ContentCaptionSection
              allFiles={allFiles}
              bundleFiles={bundleFiles}
              selectedOption={selectedOption}
              canCharge={canCharge}
              userRole={userRole}
              filePrices={filePrices}
              priceInputs={priceInputs}
              bundleCaption={bundleCaption}
              bundlePrice={bundlePrice}
              bundlePriceInput={bundlePriceInput}
              selectedTaggedUsersByFile={selectedTaggedUsersByFile}
              selectedTaggedUsersBundle={selectedTaggedUsersBundle}
              priceValidationAttempted={priceValidationAttempted}
              forCreatorMessaging={forCreatorMessaging}
              onCaptionChange={handleChangeCaption}
              onFileRemove={removeFile}
              onPriceInputChange={handlePriceInputChange}
              onSetPrice={handleSetButtonClick}
              onBundleCaptionChange={handleBundleCaptionChange}
              onBundlePriceInputChange={handleBundlePriceInputChange}
              onBundleSetPrice={handleBundleSetButtonClick}
              onTaggedUsersChange={handleTaggedUsersChange}
              onBundleTaggedUsersChange={handleBundleTaggedUsersChange}
            />

          </div>

          {/* Bottom Action Bar */}
          <div className="bottom-action-bar">
            {loading && (
              <div className="progress-container">
                <div className="progress-bar-wrapper">
                  <div
                    className="progress-bar-inner"
                    style={{ '--progress-width': `${uploadProgress}%` }}
                  ></div>
                </div>
                <span className="progress-percentage">{uploadProgress}%</span>
              </div>
            )}
            <div className="d-flex align-items-center justify-content-between gap-3">
              <button
                className="action-button action-button-cancel w-100"
                type="button"
                onClick={closeBtn}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className={`w-100 ${loading ? "upload-button-loading" : "action-button action-button-upload"}`}
                type="button"
                disabled={loading || (allFiles.length < 1 && bundleFiles.length < 1)}
                onClick={handleUploadContent}
              >
                {loading ? "Uploading..." : "Upload Content"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

// Wrapper component to use theme context
const UploadContentWithTheme = (props) => {
  const { isDarkMode } = useTheme();
  return <UploadContent {...props} isDarkMode={isDarkMode} />;
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps, { PostContent })(UploadContentWithTheme);
